import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import BackButton from '../../components/ui/BackButton';
import ChecklistSelector from './components/ChecklistSelector';
import ChecklistCompletion from './components/ChecklistCompletion';
import CompletionHistory from './components/CompletionHistory';
import { checklistService } from '../../services/checklistService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';

const DailySiteChecklist = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const siteIdParam = searchParams?.get('siteId');
  const { userProfile } = useAuth();
  
  const [activeView, setActiveView] = useState('checklist');
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(siteIdParam || '');
  const { status: siteStatus, isReadOnly: siteIsReadOnly } = useSiteReadOnly(selectedSiteId || null);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.slice(0, 10));

  const isWorker = userProfile?.role === 'worker';
  const homePath = isWorker ? '/worker-today' : userProfile?.role === 'site_manager' ? '/site-manager-dashboard' : '/admin-dashboard';

  const breadcrumbItems = [
    { label: 'Home', href: homePath },
    { label: 'Daily Site Checklist' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadChecklists(selectedSiteId);
    }
  }, [selectedSiteId]);

  useEffect(() => {
    if (selectedSiteId && selectedDate) {
      loadCompletions(selectedSiteId, selectedDate);
    }
  }, [selectedSiteId, selectedDate]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      let siteList = [];

      if (isWorker) {
        // Workers: use direct site assignments first (worker_site_assignments)
        const workerSites = await siteService?.getSitesByUser();
        siteList = workerSites?.data || [];

        if (siteList?.length > 0 && siteList?.[0]?.company_id) {
          setCompanyId(siteList?.[0]?.company_id);
        } else {
          // Fallback: company lookup
          const companyResult = await companyService?.getUserCompany();
          if (companyResult?.data?.id) {
            setCompanyId(companyResult?.data?.id);
            const companySites = await siteService?.getSitesByCompany(companyResult?.data?.id);
            siteList = companySites?.data || [];
          }
        }
      } else {
        // Admin / site_manager: company-scoped lookup (reliable for site_manager)
        const companyResult = await companyService?.getUserCompany();
        if (companyResult?.data?.id) {
          setCompanyId(companyResult?.data?.id);
          const sitesResult = await siteService?.getSitesByCompany(companyResult?.data?.id);
          siteList = sitesResult?.data || [];
        }

        // Fallback: direct user assignments
        if (!siteList?.length) {
          const directResult = await siteService?.getSitesByUser();
          siteList = directResult?.data || [];
          if (siteList?.length > 0 && siteList?.[0]?.company_id) {
            setCompanyId(siteList?.[0]?.company_id);
          }
        }
      }

      if (siteList?.length > 0) {
        setSites(siteList);
        if (siteIdParam && siteList?.some(s => s?.id === siteIdParam)) {
          setSelectedSiteId(siteIdParam);
        } else if (!siteIdParam) {
          setSelectedSiteId(siteList?.[0]?.id);
        }
      } else {
        // Last resort: all sites (admin fallback)
        if (!isWorker) {
          const allSites = await siteService?.getAllSites();
          if (allSites?.data) {
            setSites(allSites?.data);
            if (!siteIdParam && allSites?.data?.length > 0) {
              setSelectedSiteId(allSites?.data?.[0]?.id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChecklists = async (siteId) => {
    try {
      const result = await checklistService?.getChecklistsBySite(siteId);
      if (result?.data) {
        setChecklists(result?.data);
        if (result?.data?.length > 0 && !selectedChecklistId) {
          setSelectedChecklistId(result?.data?.[0]?.id);
        }
      }
    } catch (err) {
      console.error('Error loading checklists:', err);
    }
  };

  const loadCompletions = async (siteId, date) => {
    try {
      const result = await checklistService?.getChecklistCompletions(siteId, date);
      if (result?.data) {
        setCompletions(result?.data);
      }
    } catch (err) {
      console.error('Error loading completions:', err);
    }
  };

  const handleChecklistComplete = async () => {
    await loadCompletions(selectedSiteId, selectedDate);
    setActiveView('history');
  };

  return (
    <>
      <Helmet>
        <title>Daily Site Checklist - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <BackButton fallback={homePath} />
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <SiteReadOnlyBanner status={siteStatus} siteId={selectedSiteId} className="mb-6" />

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Daily Site Checklist
              </h1>
              <p className="text-sm text-muted-foreground">
                Complete daily safety and compliance checks
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeView === 'checklist' ? 'default' : 'outline'}
                onClick={() => setActiveView('checklist')}
                iconName="CheckSquare"
              >
                Complete Checklist
              </Button>
              <Button
                variant={activeView === 'history' ? 'default' : 'outline'}
                onClick={() => setActiveView('history')}
                iconName="History"
              >
                View History
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          ) : (
            <>
              {activeView === 'checklist' && (
                <div className="space-y-6">
                  <ChecklistSelector
                    sites={sites}
                    checklists={checklists}
                    selectedSiteId={selectedSiteId}
                    selectedChecklistId={selectedChecklistId}
                    onSiteChange={setSelectedSiteId}
                    onChecklistChange={setSelectedChecklistId}
                  />

                  {selectedChecklistId && !siteIsReadOnly && (
                    <ChecklistCompletion
                      checklistId={selectedChecklistId}
                      siteId={selectedSiteId}
                      companyId={companyId}
                      onComplete={handleChecklistComplete}
                    />
                  )}
                </div>
              )}

              {activeView === 'history' && (
                <CompletionHistory
                  completions={completions}
                  sites={sites}
                  selectedSiteId={selectedSiteId}
                  selectedDate={selectedDate}
                  onSiteChange={setSelectedSiteId}
                  onDateChange={setSelectedDate}
                />
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default DailySiteChecklist;